import { useQueries } from "@tanstack/react-query";
import {
  fetchAnalysisData,
  fetchIssuesData,
  fetchAnalysisPctData,
  fetchIssuesPctData,
  fetchUserData,
  fetchSessionData,
  fetchVideoData,
} from "./fetchFunctions";
import { getAllDates } from "./utils";
import firebase from "../../../common/firebase";

// Query key factory
const summaryKeys = {
  analysis: (dates, period) => ["summary", "analysis", dates, period],
  issues: (dates, period) => ["summary", "issues", dates, period],
  analysisPct: (dates, period) => ["summary", "analysisPct", dates, period],
  issuesPct: (dates, period) => ["summary", "issuesPct", dates, period],
  users: (dates, period) => ["summary", "users", dates, period],
  sessions: (dates, period) => ["summary", "sessions", dates, period],
  videos: (dates, period) => ["summary", "videos", dates, period],
  screenRecordings: (dates, period) => [
    "summary",
    "screenRecordings",
    dates,
    period,
  ],
};

// Helper function to create date ranges for queries
const createDateRanges = (dates) => {
  return dates.map((date) => ({
    start: date.start || date,
    end: date.end || date,
  }));
};

// Custom hook for analysis data
export const useAnalysisData = (startDate, endDate, period) => {
  // Transform period from "byDays" to "days" format
  const transformedPeriod = period?.replace("by", "").toLowerCase();
  const dates = getAllDates(startDate, endDate, transformedPeriod);

  // Handle null or empty dates
  if (!dates || dates.length === 0) {
    return {
      data: [],
      isLoading: false,
      isError: true,
      error: new Error(`Invalid period or date range: ${period}`),
    };
  }

  const queries = useQueries({
    queries: dates.map((date, index) => ({
      queryKey: ["analysis", date, index],
      queryFn: async () => {
        const dateRange =
          typeof date === "string" ? { start: date, end: date } : date;

        const analysisData = [0];
        const analysisDataFON = [0];
        const analysisDataDTL = [0];

        await fetchAnalysisData(
          dateRange,
          0,
          analysisData,
          analysisDataFON,
          analysisDataDTL
        );

        return {
          date,
          total: analysisData[0],
          fon: analysisDataFON[0],
          dtl: analysisDataDTL[0],
        };
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
    })),
  });

  const isLoading = queries.some((query) => query.isLoading);
  const isError = queries.some((query) => query.isError);
  const error = queries.find((query) => query.error)?.error;

  const data = queries?.map((query) => query.data).filter(Boolean);

  return {
    data,
    isLoading,
    isError,
    error,
  };
};

// Custom hook for issues data
export const useIssuesData = (startDate, endDate, period) => {
  // Transform period from "byDays" to "days" format
  const transformedPeriod = period?.replace("by", "").toLowerCase();
  const dates = getAllDates(startDate, endDate, transformedPeriod);

  // Handle null or empty dates
  if (!dates || dates.length === 0) {
    return {
      data: [],
      isLoading: false,
      isError: true,
      error: new Error(`Invalid period or date range: ${period}`),
    };
  }

  const queries = useQueries({
    queries: dates.map((date, index) => ({
      queryKey: ["issues", date, index],
      queryFn: async () => {
        const dateRange =
          typeof date === "string" ? { start: date, end: date } : date;

        const issuesData = [0];
        const issuesDataFON = [0];
        const issuesDataDTL = [0];

        await fetchIssuesData(
          dateRange,
          0,
          issuesData,
          issuesDataFON,
          issuesDataDTL
        );

        return {
          date,
          total: issuesData[0],
          fon: issuesDataFON[0],
          dtl: issuesDataDTL[0],
        };
      },
      staleTime: 5 * 60 * 1000,
    })),
  });

  const isLoading = queries.some((query) => query.isLoading);
  const isError = queries.some((query) => query.isError);
  const error = queries.find((query) => query.error)?.error;

  const data = queries.map((query) => query.data).filter(Boolean);

  return {
    data,
    isLoading,
    isError,
    error,
  };
};

// Custom hook for analysis percentage data
export const useAnalysisPctData = (startDate, endDate, period) => {
  // Transform period from "byDays" to "days" format
  const transformedPeriod = period?.replace("by", "").toLowerCase();
  const dates = getAllDates(startDate, endDate, transformedPeriod);

  // Handle null or empty dates
  if (!dates || dates.length === 0) {
    return {
      data: [],
      isLoading: false,
      isError: true,
      error: new Error(`Invalid period or date range: ${period}`),
    };
  }

  const queries = useQueries({
    queries: dates.map((date, index) => ({
      queryKey: ["analysisPct", date, index],
      queryFn: async () => {
        const dateRange =
          typeof date === "string" ? { start: date, end: date } : date;

        const analysisData = [0];
        const analysisDataFON = [0];
        const analysisDataDTL = [0];

        await fetchAnalysisPctData(
          dateRange,
          0,
          analysisData,
          analysisDataFON,
          analysisDataDTL
        );

        return {
          date,
          total: analysisData[0],
          fon: analysisDataFON[0],
          dtl: analysisDataDTL[0],
        };
      },
      staleTime: 5 * 60 * 1000,
    })),
  });

  const isLoading = queries.some((query) => query.isLoading);
  const isError = queries.some((query) => query.isError);
  const error = queries.find((query) => query.error)?.error;

  const data = queries.map((query) => query.data).filter(Boolean);

  return {
    data,
    isLoading,
    isError,
    error,
  };
};

// Custom hook for issues percentage data
export const useIssuesPctData = (startDate, endDate, period) => {
  // Transform period from "byDays" to "days" format
  const transformedPeriod = period?.replace("by", "").toLowerCase();
  const dates = getAllDates(startDate, endDate, transformedPeriod);

  // Handle null or empty dates
  if (!dates || dates.length === 0) {
    return {
      data: [],
      isLoading: false,
      isError: true,
      error: new Error(`Invalid period or date range: ${period}`),
    };
  }

  const queries = useQueries({
    queries: dates.map((date, index) => ({
      queryKey: ["issuesPct", date, index],
      queryFn: async () => {
        const dateRange =
          typeof date === "string" ? { start: date, end: date } : date;

        const issuesData = [0];
        const issuesDataFON = [0];
        const issuesDataDTL = [0];

        await fetchIssuesPctData(
          dateRange,
          0,
          issuesData,
          issuesDataFON,
          issuesDataDTL
        );

        return {
          date,
          total: issuesData[0],
          fon: issuesDataFON[0],
          dtl: issuesDataDTL[0],
        };
      },
      staleTime: 5 * 60 * 1000,
    })),
  });

  const isLoading = queries.some((query) => query.isLoading);
  const isError = queries.some((query) => query.isError);
  const error = queries.find((query) => query.error)?.error;

  const data = queries.map((query) => query.data).filter(Boolean);

  return {
    data,
    isLoading,
    isError,
    error,
  };
};

// Custom hook for user data
export const useUserData = (startDate, endDate, period) => {
  // Transform period from "byDays" to "days" format
  const transformedPeriod = period?.replace("by", "").toLowerCase();
  const dates = getAllDates(startDate, endDate, transformedPeriod);

  // Handle null or empty dates
  if (!dates || dates.length === 0) {
    return {
      data: [],
      isLoading: false,
      isError: true,
      error: new Error(`Invalid period or date range: ${period}`),
    };
  }

  const queries = useQueries({
    queries: dates.flatMap((date, index) => {
      const dateRange =
        typeof date === "string" ? { start: date, end: date } : date;

      return [
        // Total users
        {
          queryKey: ["users", "total", date, index],
          queryFn: async () => {
            const dataArr = [0];
            await fetchUserData(dateRange, 0, dataArr, "users");
            return { date, type: "total", count: dataArr[0] };
          },
          staleTime: 5 * 60 * 1000,
        },
        // Coaches
        {
          queryKey: ["users", "coaches", date, index],
          queryFn: async () => {
            const dataArr = [0];
            await fetchUserData(dateRange, 0, dataArr, "coaches", [
              "LITE_MONTHLY",
              "LITE_ANNUALLY",
              "PREMIUM_MONTHLY",
              "PREMIUM_ANNUALLY",
              "ENTERPRISE_MASTER",
              "ENTERPRISE_COACH",
              "STUDIO_MASTER",
              "STUDIO_COACH",
            ]);
            return { date, type: "coaches", count: dataArr[0] };
          },
          staleTime: 5 * 60 * 1000,
        },
        // Students
        {
          queryKey: ["users", "students", date, index],
          queryFn: async () => {
            const dataArr = [0];
            await fetchUserData(dateRange, 0, dataArr, "students", [
              "",
              "STUDENT_FREE",
              "STUDENT_LITE_MONTHLY",
              "STUDENT_LITE_ANNUALLY",
            ]);
            return { date, type: "students", count: dataArr[0] };
          },
          staleTime: 5 * 60 * 1000,
        },
        // Free students
        {
          queryKey: ["users", "freeStudents", date, index],
          queryFn: async () => {
            const dataArr = [0];
            await fetchUserData(dateRange, 0, dataArr, "students", [
              "",
              "STUDENT_FREE",
            ]);
            return { date, type: "freeStudents", count: dataArr[0] };
          },
          staleTime: 5 * 60 * 1000,
        },
        // Paid students
        {
          queryKey: ["users", "paidStudents", date, index],
          queryFn: async () => {
            const dataArr = [0];
            await fetchUserData(dateRange, 0, dataArr, "students", [
              "STUDENT_LITE_MONTHLY",
              "STUDENT_LITE_ANNUALLY",
            ]);
            return { date, type: "paidStudents", count: dataArr[0] };
          },
          staleTime: 5 * 60 * 1000,
        },
      ];
    }),
  });

  const isLoading = queries.some((query) => query.isLoading);
  const isError = queries.some((query) => query.isError);
  const error = queries.find((query) => query.error)?.error;

  // Group data by date
  const dataByDate = {};
  queries.forEach((query) => {
    if (query.data) {
      const { date, type, count } = query.data;
      if (!dataByDate[date]) {
        dataByDate[date] = { date };
      }
      dataByDate[date][type] = count;
    }
  });

  const data = Object.values(dataByDate);

  return {
    data,
    isLoading,
    isError,
    error,
  };
};

// Custom hook for session data
export const useSessionData = (startDate, endDate, period) => {
  // Transform period from "byDays" to "days" format
  const transformedPeriod = period?.replace("by", "").toLowerCase();
  const dates = getAllDates(startDate, endDate, transformedPeriod);

  // Handle null or empty dates
  if (!dates || dates.length === 0) {
    return {
      data: [],
      isLoading: false,
      isError: true,
      error: new Error(`Invalid period or date range: ${period}`),
    };
  }

  const queries = useQueries({
    queries: dates.map((date, index) => ({
      queryKey: ["sessions", date, index],
      queryFn: async () => {
        const dateRange =
          typeof date === "string" ? { start: date, end: date } : date;

        const sessionsData = [0];
        await fetchSessionData(dateRange, 0, sessionsData);

        return {
          date,
          total: sessionsData[0],
        };
      },
      staleTime: 5 * 60 * 1000,
    })),
  });

  const isLoading = queries.some((query) => query.isLoading);
  const isError = queries.some((query) => query.isError);
  const error = queries.find((query) => query.error)?.error;

  const data = queries.map((query) => query.data).filter(Boolean);

  return {
    data,
    isLoading,
    isError,
    error,
  };
};

// Custom hook for video data
export const useVideoData = (startDate, endDate, period) => {
  // Transform period from "byDays" to "days" format
  const transformedPeriod = period?.replace("by", "").toLowerCase();
  const dates = getAllDates(startDate, endDate, transformedPeriod);

  // Handle null or empty dates
  if (!dates || dates.length === 0) {
    return {
      data: [],
      isLoading: false,
      isError: true,
      error: new Error(`Invalid period or date range: ${period}`),
    };
  }

  const queries = useQueries({
    queries: dates.map((date, index) => ({
      queryKey: ["videos", date, index],
      queryFn: async () => {
        const dateRange =
          typeof date === "string" ? { start: date, end: date } : date;

        const videosData = [0];
        const videosDataFON = [0];
        const videosDataDTL = [0];

        await fetchVideoData(
          dateRange,
          0,
          videosData,
          videosDataFON,
          videosDataDTL
        );

        return {
          date,
          total: videosData[0],
          fon: videosDataFON[0],
          dtl: videosDataDTL[0],
        };
      },
      staleTime: 5 * 60 * 1000,
    })),
  });

  const isLoading = queries.some((query) => query.isLoading);
  const isError = queries.some((query) => query.isError);
  const error = queries.find((query) => query.error)?.error;

  const data = queries.map((query) => query.data).filter(Boolean);

  return {
    data,
    isLoading,
    isError,
    error,
  };
};

// Custom hook for screen recordings
export const useScreenRecordingsData = (startDate, endDate, period) => {
  // Transform period from "byDays" to "days" format
  const transformedPeriod = period?.replace("by", "").toLowerCase();
  const dates = getAllDates(startDate, endDate, transformedPeriod);

  // Handle null or empty dates
  if (!dates || dates.length === 0) {
    return {
      data: [],
      isLoading: false,
      isError: true,
      error: new Error(`Invalid period or date range: ${period}`),
    };
  }

  const queries = useQueries({
    queries: dates.map((date, index) => ({
      queryKey: ["screenRecordings", date, index],
      queryFn: async () => {
        const dateRange =
          typeof date === "string" ? { start: date, end: date } : date;

        try {
          const db = firebase.firestore();

          const snapshot = await db
            .collection("userPosts")
            .where("postType", "==", "screenRecording")
            .where("createdAt", ">=", dateRange.start)
            .where("createdAt", "<=", dateRange.end)
            .get();

          return {
            date,
            total: snapshot.size || 0,
          };
        } catch (error) {
          console.error("Error fetching screen recordings data:", error);
          return {
            date,
            total: 0,
          };
        }
      },
      staleTime: 5 * 60 * 1000,
    })),
  });

  const isLoading = queries.some((query) => query.isLoading);
  const isError = queries.some((query) => query.isError);
  const error = queries.find((query) => query.error)?.error;

  const data = queries.map((query) => query.data).filter(Boolean);

  return {
    data,
    isLoading,
    isError,
    error,
  };
};
